/**
 * Register Page
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { InputWithIcon } from '@shared/components/form'
import { PhoneInput } from '@shared/components/forms'
import { TermsOfServiceModal } from '@shared/components/TermsOfServiceModal'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { Checkbox } from '@shared/components/ui/checkbox'
import { useAuth } from '@shared/contexts'
import { registerSchema, type RegisterFormData } from '@shared/lib/validators'
import { Info, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function RegisterPage() {
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      loginWithPhone: true, // phone-only prototype: no toggle, always true
      toc: false,
      email: '',
      phoneNumber: '',
      password: '',
      repeatPassword: '',
    },
  })

  const tocValue = watch('toc')
  const phoneNumber = watch('phoneNumber') || ''

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)

    try {
      // Prepare phone number with Argentina prefix if needed
      let identifier = data.phoneNumber || ''
      const cleaned = identifier.replace(/\D/g, '')
      if (cleaned.length === 10) {
        identifier = '549' + cleaned
      }

      await registerUser({
        email: identifier,
        phone: identifier,
        password: data.password,
        confirmPassword: data.repeatPassword,
        firstName: '', // Will be filled in profile later
        lastName: '',
      }, 'phone')

      toast.success('Cuenta creada exitosamente!')

      void navigate('/')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al crear la cuenta')
    } finally {
      setIsLoading(false)
    }
  }

  const setPhoneNumber = (v: string) => setValue('phoneNumber', v)
  const setToc = (v: boolean) => setValue('toc', v)

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
      <Card className="w-full max-w-md p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-center mb-6">Crear Cuenta</h1>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="Número de teléfono"
            />
            {errors.phoneNumber && (
              <p className="text-sm text-destructive mt-1">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div>
            <InputWithIcon
              isPasswordToggle
              id="password"
              type="password"
              placeholder="Contraseña"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <InputWithIcon
              isPasswordToggle
              id="repeatPassword"
              type="password"
              placeholder="Confirmar contraseña"
              {...register('repeatPassword')}
            />
            {errors.repeatPassword && (
              <p className="text-sm text-destructive mt-1">{errors.repeatPassword.message}</p>
            )}
          </div>

          <div className="bg-muted p-3 rounded-md flex gap-2">
            <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Importante</p>
              <p>• Contraseña de más de 8 caracteres</p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox checked={tocValue} onCheckedChange={(c) => setToc(!!c)} />
            <label className="text-sm leading-none cursor-pointer">
              Acepto los{' '}
              <button type="button" onClick={() => setShowTermsModal(true)} className="underline">
                términos y condiciones
              </button>{' '}
              de uso
            </label>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creando cuenta...' : (<><UserPlus className="mr-2 h-4 w-4" />Registrarme</>)}
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="text-primary hover:underline">Iniciar sesión</Link>
          </div>
        </form>
      </Card>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal open={showTermsModal} onOpenChange={setShowTermsModal} />
    </div>
  )
}
